import { useMemo, useState } from "react";
import { databaseMigrationsApi } from "../../../../api/databaseMigrations";
import { deriveStatus } from "./utils/statusDerivation";
import MigrationsToolbar from "./MigrationsToolbar";
import type { MigrationRowData, TargetSummary, MigrationStatusKey } from "./utils/constants";

interface MigrationsListProps {
  tabKind: "tenant" | "registry";
  rows: MigrationRowData[];
  availableTargets: TargetSummary[];
  selectedTargetIds: string[];
  onTargetsChange: (ids: string[]) => void;
  onApplyMigration: (row: MigrationRowData, targetIds: string[]) => void;
  onPreviewMigration?: (row: MigrationRowData) => void;
  onBulkApply: (rows: MigrationRowData[], targetIds: string[]) => void;
  onMarkApplied: (row: MigrationRowData) => void;
  loading?: boolean;
  onArchiveChanged: () => void;
}

function displayName(row: MigrationRowData): string {
  const parts = row.folderName.split("/");
  return parts[parts.length - 1].replace(/^migrat(?:e|ions)-\d{4}-\d{1,2}-\d{1,2}-?/, "") || row.folderName;
}

// Everything except "applied" — mirrors the old default "Active" view (don't
// open on a wall of things already done), but as a real multi-select instead
// of a single tab, so e.g. "New" + "Failed" can be viewed together.
// "already-present" is visible by default: it needs one click to confirm, and
// hiding it is what let these sit in the list looking unapplied forever.
const DEFAULT_VISIBLE_STATUSES = new Set<MigrationStatusKey>([
  "new", "partial", "changed", "failed", "already-present",
]);

export default function MigrationsList({
  tabKind,
  rows,
  availableTargets,
  selectedTargetIds,
  onTargetsChange,
  onApplyMigration,
  onPreviewMigration,
  onBulkApply,
  onMarkApplied,
  loading,
  onArchiveChanged,
}: MigrationsListProps) {
  const [search, setSearch] = useState("");
  const [visibleStatuses, setVisibleStatuses] = useState<Set<MigrationStatusKey>>(DEFAULT_VISIBLE_STATUSES);
  const [inferredFilter, setInferredFilter] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const isBlue = tabKind === "tenant";
  const accent = isBlue
    ? { bulk: "bg-blue-50 border-blue-200 text-blue-900", bbtn: "border-blue-200 text-blue-700", action: "text-blue-600 hover:text-blue-800" }
    : { bulk: "bg-purple-50 border-purple-200 text-purple-900", bbtn: "border-purple-200 text-purple-700", action: "text-purple-600 hover:text-purple-800" };

  const selectedTargetIdSet = useMemo(() => new Set(selectedTargetIds), [selectedTargetIds]);
  const selectedDatabaseNames = useMemo(
    () => availableTargets.filter((t) => selectedTargetIds.includes(t.id)).map((t) => t.databaseName),
    [availableTargets, selectedTargetIds],
  );

  // With no company picked, judge a migration against EVERY company on this
  // tab: before, one success anywhere made it "applied" (hidden by default),
  // so a migration run on a single company vanished from the list while the
  // rest still needed it (user report 2026-10-18). Now it shows as partial.
  const statusDatabaseNames = useMemo(
    () => (selectedDatabaseNames.length > 0 ? selectedDatabaseNames : availableTargets.map((t) => t.databaseName)),
    [selectedDatabaseNames, availableTargets],
  );

  const nonArchivedRows = useMemo(() => rows.filter((r) => !r.archived), [rows]);

  // Scope-inferred (no `-- @scope:` header — a real, large backlog on this
  // codebase: most migration folders predate the tagging convention) is its
  // own opt-in review lane, not part of the default "things to do" view.
  // Previously these counted into both "New" and "Inferred" simultaneously
  // with no way to exclude them, so the New count was dominated by files
  // nothing could ever bulk-apply (scope-inferred rows are barred from Run/
  // Apply since their tenant-vs-registry scope was never confirmed) — the
  // page looked like it had a huge backlog that Apply could never touch.
  const inferredCount = useMemo(
    () => nonArchivedRows.filter((r) => r.scopeInferred).length,
    [nonArchivedRows],
  );
  const visibleBaseRows = useMemo(
    () => nonArchivedRows.filter((r) => r.scopeInferred === inferredFilter),
    [nonArchivedRows, inferredFilter],
  );

  const statusCounts = useMemo(() => {
    const counts = { new: 0, partial: 0, changed: 0, failed: 0, "already-present": 0, applied: 0 } as Record<MigrationStatusKey, number>;
    for (const row of visibleBaseRows) counts[deriveStatus(row, statusDatabaseNames)]++;
    return counts;
  }, [visibleBaseRows, statusDatabaseNames]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = showArchived ? rows.filter((r) => r.archived) : visibleBaseRows;
    return base.filter((row) => {
      if (!showArchived) {
        const status = deriveStatus(row, statusDatabaseNames);
        if (!visibleStatuses.has(status)) return false;
      }
      if (q) {
        const hay = `${row.folderName} ${row.fileName}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, visibleBaseRows, search, visibleStatuses, showArchived, statusDatabaseNames]);

  // What the toolbar's primary "Apply" button would actually run: every
  // filtered, non-archived row not already applied to every selected target,
  // excluding inferred-scope ones (their scope wasn't confirmed by a real
  // `-- @scope:` header, so a one-click bulk apply shouldn't guess).
  const bulkApplyCandidates = useMemo(
    () => filtered.filter((row) => {
      if (row.scopeInferred) return false;
      const status = deriveStatus(row, selectedDatabaseNames);
      // Re-running an "already-present" migration just reproduces the same
      // duplicate-object error. Confirming it is a separate, explicit action.
      return status !== "applied" && status !== "already-present";
    }),
    [filtered, selectedDatabaseNames],
  );
  const bulkSkippedCount = useMemo(
    () => filtered.filter((row) => {
      const status = deriveStatus(row, selectedDatabaseNames);
      return row.scopeInferred && status !== "applied" && status !== "already-present";
    }).length,
    [filtered, selectedDatabaseNames],
  );
  const bulkApplyLabel = selectedTargetIds.length === 0
    ? "Select targets to apply"
    : bulkApplyCandidates.length === 0
    ? "Nothing to apply"
    : `Apply ${bulkApplyCandidates.length} to ${selectedTargetIds.length} target${selectedTargetIds.length === 1 ? "" : "s"}`;

  const allIds = useMemo(() => filtered.map((r) => r.id), [filtered]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));

  function toggleAll() {
    if (allSelected) {
      setSelected((prev) => { const next = new Set(prev); allIds.forEach((id) => next.delete(id)); return next; });
    } else {
      setSelected((prev) => { const next = new Set(prev); allIds.forEach((id) => next.add(id)); return next; });
    }
  }

  function toggleRow(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const selectedRows = useMemo(() => filtered.filter((r) => selected.has(r.id)), [filtered, selected]);
  const selectedFolderPaths = selectedRows.map((r) => r.folderPath);

  async function handleArchiveSelected() {
    if (selectedFolderPaths.length === 0) return;
    try {
      await databaseMigrationsApi.archiveMigrations(selectedFolderPaths);
      setSelected(new Set());
      onArchiveChanged();
    } catch (e) {
      setArchiveError(e instanceof Error ? e.message : 'Failed to archive migrations');
    }
  }

  async function handleUnarchiveSelected() {
    if (selectedFolderPaths.length === 0) return;
    try {
      await databaseMigrationsApi.unarchiveMigrations(selectedFolderPaths);
      setSelected(new Set());
      onArchiveChanged();
    } catch (e) {
      setArchiveError(e instanceof Error ? e.message : 'Failed to unarchive migrations');
    }
  }

  async function handleArchiveRow(row: MigrationRowData) {
    try {
      await databaseMigrationsApi.archiveMigrations([row.folderPath]);
      onArchiveChanged();
    } catch (e) {
      setArchiveError(e instanceof Error ? e.message : 'Failed to archive migration');
    }
  }

  async function handleUnarchiveRow(row: MigrationRowData) {
    try {
      await databaseMigrationsApi.unarchiveMigrations([row.folderPath]);
      onArchiveChanged();
    } catch (e) {
      setArchiveError(e instanceof Error ? e.message : 'Failed to unarchive migration');
    }
  }

  function toggleStatus(status: MigrationStatusKey) {
    setVisibleStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status); else next.add(status);
      return next;
    });
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      {/* No overflow-hidden on this outer wrapper — the target-picker
          dropdown below is an absolutely-positioned child that extends past
          the toolbar's own bounds, and clipping the wrapper clips the
          dropdown with it (only its own internal list was scrollable, the
          dropdown panel itself was being cut off). MigrationsToolbar's root
          carries its own rounded-t-xl so the top corners still look clean
          without needing that clip. */}
      <MigrationsToolbar
        search={search}
        onSearchChange={setSearch}
        visibleStatuses={visibleStatuses}
        onStatusToggle={toggleStatus}
        inferredFilter={inferredFilter}
        onInferredToggle={() => setInferredFilter((v) => !v)}
        inferredCount={inferredCount}
        statusCounts={statusCounts}
        availableTargets={availableTargets}
        selectedTargetIds={selectedTargetIdSet}
        onTargetsChange={(next) => onTargetsChange(Array.from(next))}
        bulkApplyEnabled={!loading && selectedTargetIds.length > 0 && bulkApplyCandidates.length > 0}
        bulkApplyLabel={bulkApplyLabel}
        bulkSkippedCount={bulkSkippedCount}
        onBulkApplyClick={() => onBulkApply(bulkApplyCandidates, selectedTargetIds)}
      />

      {/* Everything below never overflows its own bounds, so it's safe to
          clip for clean bottom corners. */}
      <div className="rounded-b-xl overflow-hidden">
      {/* Secondary row: archived view + row-selection bulk actions live here,
          since they're a distinct dimension from the status/target filters above. */}
      <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-3 text-[12px]">
        <button
          type="button"
          onClick={() => { setShowArchived((v) => !v); setSelected(new Set()); }}
          className={`font-semibold ${showArchived ? "text-slate-900" : "text-slate-400 hover:text-slate-600"}`}
        >
          {showArchived ? "← Back to active" : "View archived"}
        </button>
        {selectedRows.length > 0 && (
          <div className={`ml-auto flex items-center gap-2 px-3 py-1 rounded-lg border ${accent.bulk}`}>
            <span className="font-semibold">{selectedRows.length} selected</span>
            {showArchived ? (
              <button type="button" onClick={handleUnarchiveSelected} className={`px-2.5 py-1 rounded-md border bg-white text-[11px] font-semibold ${accent.bbtn}`}>
                Unarchive
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => onBulkApply(selectedRows, selectedTargetIds)}
                  disabled={loading || selectedTargetIds.length === 0}
                  title={selectedTargetIds.length === 0 ? "Select at least one target first" : undefined}
                  className={`px-2.5 py-1 rounded-md border bg-white text-[11px] font-semibold disabled:opacity-40 ${accent.bbtn}`}
                >
                  Mark applied
                </button>
                <button type="button" onClick={handleArchiveSelected} className={`px-2.5 py-1 rounded-md border bg-white text-[11px] font-semibold ${accent.bbtn}`}>
                  Archive
                </button>
                <button
                  type="button"
                  onClick={() => onBulkApply(selectedRows, selectedTargetIds)}
                  disabled={loading || selectedTargetIds.length === 0}
                  className={`px-2.5 py-1 rounded-md border bg-white text-[11px] font-semibold disabled:opacity-40 ${accent.bbtn}`}
                >
                  Run…
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Archive error banner */}
      {archiveError && (
        <div className="px-4 py-2 border-b border-red-200 bg-red-50 flex items-center justify-between gap-2 text-[12px] text-red-800">
          <span>{archiveError}</span>
          <button type="button" onClick={() => setArchiveError(null)} className="text-red-600 hover:text-red-800 font-semibold text-[11px]">Dismiss</button>
        </div>
      )}

      {/* Table */}
      <table className="w-full text-[13px] border-collapse">
        <thead>
          <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
            <th className="w-8 px-3 py-2.5 text-left">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded" />
            </th>
            <th className="px-3 py-2.5 text-left">Migration</th>
            <th className="px-3 py-2.5 text-left">Scope</th>
            <th className="px-3 py-2.5 text-left">Applied</th>
            <th className="px-3 py-2.5 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-[12px] text-slate-400">
                {inferredFilter
                  ? "No legacy scope-inferred migrations match the current filter."
                  : inferredCount > 0 && nonArchivedRows.length === inferredCount
                  ? `All ${inferredCount} migrations here are legacy files missing a -- @scope: header — click "Inferred scope" above to review them.`
                  : selectedTargetIds.length === 0
                  ? "No migrations match the current filter."
                  : "Nothing outstanding for the selected targets — they're all caught up."}
              </td>
            </tr>
          )}
          {filtered.map((row) => {
            const status = deriveStatus(row, statusDatabaseNames);
            const isChecked = selected.has(row.id);

            // Applied display
            let appliedDisplay;
            if (tabKind === "tenant") {
              if (selectedDatabaseNames.length === 0) {
                const totalApplied = Object.values(row.appliedTo).filter((i) => i.status === "success").length;
                appliedDisplay = <span className="text-slate-400">{totalApplied > 0 ? `${totalApplied} applied` : "—"}</span>;
              } else {
                const appliedCount = selectedDatabaseNames.filter((db) => row.appliedTo[db]?.status === "success").length;
                const n = selectedDatabaseNames.length;
                appliedDisplay = (
                  <>
                    <span className={appliedCount === n ? "text-emerald-600 font-semibold" : appliedCount === 0 ? "text-slate-400" : "text-amber-600"}>
                      {appliedCount} / {n}
                    </span>
                    <div className="text-[9.5px] text-slate-400 font-medium mt-0.5">of selected targets</div>
                  </>
                );
              }
            } else {
              appliedDisplay = status === "applied"
                ? <span className="text-emerald-600 font-semibold">Applied</span>
                : <span className="text-slate-400">Pending</span>;
            }

            const canRun = !row.scopeInferred && (tabKind === "registry" || selectedTargetIds.length > 0) && !loading && status !== "applied";

            return (
              <tr key={row.id} className={`border-t border-slate-100 hover:bg-slate-50 ${row.archived ? "opacity-60" : ""}`}>
                <td className="px-3 py-2.5">
                  <input type="checkbox" checked={isChecked} onChange={() => toggleRow(row.id)} className="rounded" />
                </td>
                <td className="px-3 py-2.5">
                  <div className="font-semibold text-slate-900 leading-snug">{displayName(row)}</div>
                  <div className="font-mono text-[11px] text-slate-500">{row.folderName}/{row.fileName}</div>
                  {row.archived && row.archivedReason && <div className="text-[11px] text-slate-500">Archived: {row.archivedReason}</div>}
                </td>
                <td className="px-3 py-2.5">
                  {row.scopeInferred ? (
                    <span className="inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-50 border border-amber-200 text-amber-700">
                      Inferred {row.scope}
                    </span>
                  ) : row.scope === "tenant" ? (
                    <span className="inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-blue-50 border border-blue-200 text-blue-700">Tenant</span>
                  ) : (
                    <span className="inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-purple-50 border border-purple-200 text-purple-700">Registry</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-[11px]">{appliedDisplay}</td>
                <td className="px-3 py-2.5 text-right">
                  <span className="inline-flex gap-2">
                    {canRun && (
                      <button
                        type="button"
                        onClick={() => onApplyMigration(row, selectedTargetIds)}
                        className={`text-[11px] font-semibold ${accent.action}`}
                      >
                        Run
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onMarkApplied(row)}
                      className={`text-[11px] font-semibold ${accent.action}`}
                    >
                      Mark
                    </button>
                    {onPreviewMigration && (
                      <button
                        type="button"
                        onClick={() => onPreviewMigration(row)}
                        className={`text-[11px] font-semibold ${accent.action}`}
                      >
                        Preview
                      </button>
                    )}
                    {row.archived ? (
                      <button
                        type="button"
                        onClick={() => handleUnarchiveRow(row)}
                        className="text-[11px] font-semibold text-slate-500 hover:text-slate-700"
                      >
                        Unarchive
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleArchiveRow(row)}
                        className="text-[11px] font-semibold text-slate-400 hover:text-slate-600"
                      >
                        Archive
                      </button>
                    )}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}
